import type { ReactNode } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command'

import type {
  GlobalSearchResultGroup,
  GlobalSearchResultItem,
  GlobalSearchResultType,
} from './global-search.interface'

interface SearchResultGroupProps {
  group: GlobalSearchResultGroup
  onSelect: (type: GlobalSearchResultType, item: GlobalSearchResultItem) => void
  isLast: boolean
}

/** Renders a categorized group of search results with icon or avatar display */
export const SearchResultGroup = ({
  group,
  onSelect,
  isLast,
}: SearchResultGroupProps): ReactNode => (
  <>
    <CommandGroup heading={group.label}>
      {group.itemList.map((item) => {
        const ItemIcon = item.icon
        const hasImage = !!item.imageUrl

        return (
          <CommandItem
            key={item.id}
            value={`${group.type}-${item.id}-${item.title}`}
            onSelect={() => onSelect(group.type, item)}
            disabled={item.isDisabled}
            className="global-search-result-item gap-3"
          >
            {hasImage ? (
              <Avatar className="global-search-result-avatar size-6 shrink-0">
                <AvatarImage src={item.imageUrl ?? undefined} alt={item.title} />
                <AvatarFallback className="text-[10px]">
                  {item.title.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            ) : (
              ItemIcon && <ItemIcon className="h-4 w-4" />
            )}
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate">{item.title}</span>
              {item.description && (
                <span className="global-search-result-description truncate text-xs text-muted-foreground">
                  {item.description}
                </span>
              )}
            </div>
            {item.shortcut && <CommandShortcut>{item.shortcut}</CommandShortcut>}
          </CommandItem>
        )
      })}
    </CommandGroup>
    {!isLast && <CommandSeparator />}
  </>
)
